import { BackSide, Color, MeshBasicMaterial } from 'three';

/**
 * NPR outline material — inverted-hull with hand-drawn jitter.
 *
 * Built as a MeshBasicMaterial + `onBeforeCompile` rather than a custom
 * ShaderMaterial. This is intentional: ShaderMaterial requires manually
 * wiring up skinning attributes/uniforms (`skinIndex`, `bindMatrix`,
 * `boneTexture`, etc.), and getting that wrong leaves the outline mesh
 * invisible or mis-deformed. MeshBasicMaterial's pre-built shader chain
 * already includes the skinning chunks correctly — we just splice in our
 * normal-displacement + position-hash jitter at the `begin_vertex` hook,
 * BEFORE the skinning chunk runs.
 *
 * Result: a SkinnedMesh's outline follows bone animation exactly, with
 * per-vertex thickness variation that reads as hand-drawn ink instead of
 * a uniform plastic shell.
 */

export function makeNPROutlineMaterial(opts: {
  thickness?: number;       // base outline thickness in object-space (default 0.005m)
  jitter?: number;          // noise jitter on thickness (default 0.35 = ±35%)
  color?: Color | string;   // outline color (default near-black ink)
} = {}): MeshBasicMaterial {
  // Default thickness 5mm — small enough that detail meshes (mask, horns, eyes)
  // aren't swallowed by the outline shell, but readable on body silhouette.
  const thickness = opts.thickness ?? 0.005;
  const jitter = opts.jitter ?? 0.35;
  const color = opts.color instanceof Color ? opts.color.clone() : new Color(opts.color ?? '#080812');

  const mat = new MeshBasicMaterial({
    color,
    side: BackSide,
    // Outline writes depth so it occludes correctly behind the inner mesh,
    // but uses LessEqual depthFunc so it doesn't fight the inner mesh's own
    // depth values — eliminates self-occlusion artifacts.
    depthWrite: true,
  });

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.u_thickness = { value: thickness };
    shader.uniforms.u_jitter = { value: jitter };

    // Patch vertex shader — inject hash + thickness uniform declarations,
    // then replace `begin_vertex` to push the position along the normal with
    // a per-vertex jittered stroke width. This runs BEFORE the skinning chunk,
    // so the displaced position then gets bone-deformed normally.
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `
        #include <common>
        uniform float u_thickness;
        uniform float u_jitter;
        // Cheap 3D hash → [0..1]
        float nprHash3(vec3 p) {
          return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
        }
        `,
      )
      .replace(
        '#include <begin_vertex>',
        `
        float nprJitter = (nprHash3(position * 17.0) - 0.5) * u_jitter * 2.0;
        float nprStroke = u_thickness * (1.0 + nprJitter);
        vec3 transformed = position + normalize(normal) * nprStroke;
        `,
      );
  };

  // Force compile-time cache miss when uniforms change — three.js otherwise
  // re-uses an old program if onBeforeCompile churns the same source.
  mat.customProgramCacheKey = () => `npr-outline-${thickness}-${jitter}`;

  return mat;
}
